import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Loader2, Send, CheckCircle } from "lucide-react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const logoUrl = "https://customer-assets.emergentagent.com/job_luxury-facial-pro/artifacts/gjd36s6k_Gemini_Generated_Image_rqozrirqozrirqoz.png";

const conditionsList = [
  "Asthma", "Fever Blisters", "Hysterectomy", "Sinus Problems", "Lupus",
  "Cardiac Problems", "Headaches (Chronic)", "Skin Disease", "Immune Disorders", "Depression",
  "Anxiety", "Hepatitis", "Herpes", "Epilepsy", "Eczema",
  "High Blood Pressure", "Metal Bone, Pins or Plates"
];

const InputField = ({ label, name, value, onChange, type = "text", required = false, placeholder = "" }) => (
  <div>
    <label htmlFor={name} className="block font-body text-xs tracking-wider uppercase text-[#A3A3A3] mb-2">
      {label} {required && "*"}
    </label>
    <input
      type={type}
      id={name}
      name={name}
      value={value}
      onChange={onChange}
      required={required}
      placeholder={placeholder}
      data-testid={`intake-${name}`}
      className="w-full luxury-input"
    />
  </div>
);

const YesNoField = ({ label, name, value, onChange, followUp = null, followUpName = "", followUpValue = "", onFollowUpChange }) => (
  <div className="flex flex-wrap items-center gap-4 py-3 border-b border-[#262626]">
    <span className="font-body text-sm text-white flex-1 min-w-[200px]">{label}</span>
    <div className="flex items-center gap-4">
      <label className="flex items-center gap-2 cursor-pointer">
        <input type="radio" name={name} value="yes" checked={value === "yes"} onChange={onChange}
          className="accent-[#C5A059]" data-testid={`intake-${name}-yes`} />
        <span className="text-sm text-[#A3A3A3]">Yes</span>
      </label>
      <label className="flex items-center gap-2 cursor-pointer">
        <input type="radio" name={name} value="no" checked={value === "no"} onChange={onChange}
          className="accent-[#C5A059]" data-testid={`intake-${name}-no`} />
        <span className="text-sm text-[#A3A3A3]">No</span>
      </label>
    </div>
    {followUp && value === "yes" && (
      <input
        type="text"
        name={followUpName}
        value={followUpValue}
        onChange={onFollowUpChange}
        placeholder={followUp}
        className="w-full luxury-input mt-2 text-sm"
      />
    )}
  </div>
);

export const ClientIntakeForm = () => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);

  const [form, setForm] = useState({
    date: new Date().toISOString().split("T")[0],
    gender: "",
    name: "", date_of_birth: "",
    address: "", city: "",
    state: "", zip: "", email: "",
    phone: "", referral: "",
    emergency_contact: "", ec_phone: "",
    physician: "", health_insurance: "",
    skin_goals: "", areas_of_concern: "", treatment_goals: "",
    first_facial: "", last_facial_date: "",
    pregnant: "",
    birth_control: "", birth_control_type: "",
    physician_care: "",
    using_products: "", products_duration: "",
    accutane: "",
    contact_lenses: "",
    medications: "", medications_list: "",
    skin_cancer: "",
    stress: "",
    smoke: "",
    allergies: "",
    skincare_products: "",
    conditions: [],
    conditions_details: "",
    signature: "", signature_date: new Date().toISOString().split("T")[0],
    minor_consent: false,
    guardian_signature: "", guardian_date: "",
  });

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleConditionToggle = (condition) => {
    setForm((prev) => ({
      ...prev,
      conditions: prev.conditions.includes(condition)
        ? prev.conditions.filter((c) => c !== condition)
        : [...prev.conditions, condition]
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    try {
      const response = await axios.post(`${API}/intake-forms`, form);
      if (response.data.success) {
        setIsSubmitted(true);
        toast.success("Intake form submitted successfully!", {
          description: "We have received your information.",
        });
      }
    } catch (error) {
      console.error("Error submitting intake form:", error);
      toast.error("Failed to submit form", {
        description: "Please try again or contact us directly.",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isSubmitted) {
    return (
      <div className="pt-20">
        <section className="py-24 md:py-32 bg-[#050505]">
          <div className="max-w-3xl mx-auto px-6 md:px-12">
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              className="bg-[#121212] border border-[#262626] p-12 text-center"
              data-testid="intake-success"
            >
              <CheckCircle className="w-16 h-16 text-[#C5A059] mx-auto mb-6" />
              <h3 className="font-heading text-2xl md:text-3xl text-white mb-4">
                Thank You
              </h3>
              <p className="font-body text-[#A3A3A3] mb-8">
                Your Client Intake Form has been submitted. We will review your information before your appointment.
              </p>
              <button
                onClick={() => { setIsSubmitted(false); setForm((prev) => ({ ...prev, date: new Date().toISOString().split("T")[0] })); }}
                data-testid="intake-submit-another"
                className="text-[#C5A059] font-body text-sm tracking-wider uppercase hover:underline"
              >
                Submit Another Form
              </button>
            </motion.div>
          </div>
        </section>
      </div>
    );
  }

  return (
    <div data-testid="intake-form-page" className="pt-20">
      {/* Hero */}
      <section className="py-16 md:py-24 bg-[#050505]">
        <div className="max-w-3xl mx-auto px-6 md:px-12 text-center">
          <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8 }}>
            <img src={logoUrl} alt="Luxury Beauty" className="h-20 w-auto mx-auto mb-6" />
            <h1 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-light text-white mb-4">
              Client Intake Form
              <span className="block text-lg sm:text-xl gold-gradient mt-2">Facial Treatment</span>
            </h1>
            <p className="font-body text-sm text-[#A3A3A3]">
              Please fill out this form completely before your appointment.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Form */}
      <section className="py-8 md:py-16 bg-[#0A0A0A]">
        <div className="max-w-4xl mx-auto px-6 md:px-12">
          <form onSubmit={handleSubmit} className="space-y-10">

            {/* Personal Information */}
            <div className="bg-[#121212] border border-[#262626] p-6 md:p-8">
              <h2 className="font-body text-xs font-semibold tracking-[0.3em] uppercase text-[#C5A059] mb-6">
                Personal Information
              </h2>

              <div className="grid md:grid-cols-2 gap-6 mb-6">
                <InputField label="Date" name="date" type="date" value={form.date} onChange={handleChange} required />
                <div>
                  <label className="block font-body text-xs tracking-wider uppercase text-[#A3A3A3] mb-2">Gender *</label>
                  <div className="flex gap-6 pt-3">
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input type="radio" name="gender" value="male" checked={form.gender === "male"} onChange={handleChange}
                        className="accent-[#C5A059]" data-testid="intake-gender-male" />
                      <span className="text-white text-sm">Male</span>
                    </label>
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input type="radio" name="gender" value="female" checked={form.gender === "female"} onChange={handleChange}
                        className="accent-[#C5A059]" data-testid="intake-gender-female" />
                      <span className="text-white text-sm">Female</span>
                    </label>
                  </div>
                </div>
              </div>

              <div className="grid md:grid-cols-2 gap-6">
                <InputField label="Name" name="name" value={form.name} onChange={handleChange} required placeholder="Full name" />
                <InputField label="Date of Birth" name="date_of_birth" type="date" value={form.date_of_birth} onChange={handleChange} required />
                <InputField label="Address" name="address" value={form.address} onChange={handleChange} placeholder="Street address" />
                <InputField label="City" name="city" value={form.city} onChange={handleChange} />
                <InputField label="State" name="state" value={form.state} onChange={handleChange} />
                <InputField label="Zip" name="zip" value={form.zip} onChange={handleChange} />
                <InputField label="Email" name="email" type="email" value={form.email} onChange={handleChange} required placeholder="Email address" />
                <InputField label="Phone" name="phone" type="tel" value={form.phone} onChange={handleChange} required placeholder="Phone number" />
                <InputField label="Referral" name="referral" value={form.referral} onChange={handleChange} placeholder="How did you hear about us?" />
                <InputField label="Emergency Contact" name="emergency_contact" value={form.emergency_contact} onChange={handleChange} placeholder="Emergency contact name" />
                <InputField label="EC Phone" name="ec_phone" type="tel" value={form.ec_phone} onChange={handleChange} placeholder="Emergency contact phone" />
                <InputField label="Physician" name="physician" value={form.physician} onChange={handleChange} placeholder="Physician name" />
                <InputField label="Health Insurance Carrier" name="health_insurance" value={form.health_insurance} onChange={handleChange} placeholder="Insurance provider" />
              </div>
            </div>

            {/* Skin Goals */}
            <div className="bg-[#121212] border border-[#262626] p-6 md:p-8">
              <h2 className="font-body text-xs font-semibold tracking-[0.3em] uppercase text-[#C5A059] mb-6">
                Skin Goals
              </h2>
              <div className="space-y-6">
                <div>
                  <label className="block font-body text-xs tracking-wider uppercase text-[#A3A3A3] mb-2">
                    What are your long-term skin goals?
                  </label>
                  <textarea name="skin_goals" value={form.skin_goals} onChange={handleChange} rows={2}
                    data-testid="intake-skin_goals" className="w-full luxury-input resize-none" />
                </div>
                <div>
                  <label className="block font-body text-xs tracking-wider uppercase text-[#A3A3A3] mb-2">
                    What are your areas of concern?
                  </label>
                  <textarea name="areas_of_concern" value={form.areas_of_concern} onChange={handleChange} rows={2}
                    data-testid="intake-areas_of_concern" className="w-full luxury-input resize-none" />
                </div>
                <div>
                  <label className="block font-body text-xs tracking-wider uppercase text-[#A3A3A3] mb-2">
                    What are your goals for this treatment?
                  </label>
                  <textarea name="treatment_goals" value={form.treatment_goals} onChange={handleChange} rows={2}
                    data-testid="intake-treatment_goals" className="w-full luxury-input resize-none" />
                </div>
              </div>
            </div>

            {/* Health Questionnaire */}
            <div className="bg-[#121212] border border-[#262626] p-6 md:p-8">
              <h2 className="font-body text-xs font-semibold tracking-[0.3em] uppercase text-[#C5A059] mb-6">
                Health Questionnaire
              </h2>
              <div className="space-y-1">
                <YesNoField label="Is this your first facial?" name="first_facial" value={form.first_facial} onChange={handleChange}
                  followUp="When was your last facial?" followUpName="last_facial_date" followUpValue={form.last_facial_date} onFollowUpChange={handleChange} />

                <YesNoField label="Are you pregnant?" name="pregnant" value={form.pregnant} onChange={handleChange} />

                <YesNoField label="Are you taking birth control pills?" name="birth_control" value={form.birth_control} onChange={handleChange}
                  followUp="If yes, what type?" followUpName="birth_control_type" followUpValue={form.birth_control_type} onFollowUpChange={handleChange} />

                <YesNoField label="Are you under a physician's care for any current skin condition or other problem?" name="physician_care" value={form.physician_care} onChange={handleChange} />

                <YesNoField label="Are you using (or have used) Azlex, Differin, Renova, Retin-A, Tazarac, Glycolic or Alpha Hydroxy Acids?" name="using_products" value={form.using_products} onChange={handleChange}
                  followUp="If yes, when and for how long?" followUpName="products_duration" followUpValue={form.products_duration} onFollowUpChange={handleChange} />

                <YesNoField label="Are you now using or have you ever used Accutane?" name="accutane" value={form.accutane} onChange={handleChange} />

                <YesNoField label="Do you wear contact lenses?" name="contact_lenses" value={form.contact_lenses} onChange={handleChange} />

                <YesNoField label="Are you presently taking any medications?" name="medications" value={form.medications} onChange={handleChange}
                  followUp="If yes, please list" followUpName="medications_list" followUpValue={form.medications_list} onFollowUpChange={handleChange} />

                <YesNoField label="Have you had skin cancer?" name="skin_cancer" value={form.skin_cancer} onChange={handleChange} />

                <YesNoField label="Do you often experience stress?" name="stress" value={form.stress} onChange={handleChange} />

                <YesNoField label="Do you smoke?" name="smoke" value={form.smoke} onChange={handleChange} />
              </div>

              <div className="mt-6 space-y-6">
                <div>
                  <label className="block font-body text-xs tracking-wider uppercase text-[#A3A3A3] mb-2">
                    Please list any allergies
                  </label>
                  <input type="text" name="allergies" value={form.allergies} onChange={handleChange}
                    data-testid="intake-allergies" className="w-full luxury-input" placeholder="List any allergies..." />
                </div>
                <div>
                  <label className="block font-body text-xs tracking-wider uppercase text-[#A3A3A3] mb-2">
                    What skin care products do you use presently?
                  </label>
                  <input type="text" name="skincare_products" value={form.skincare_products} onChange={handleChange}
                    data-testid="intake-skincare_products" className="w-full luxury-input" placeholder="Current skincare products..." />
                </div>
              </div>
            </div>

            {/* Medical Conditions */}
            <div className="bg-[#121212] border border-[#262626] p-6 md:p-8">
              <h2 className="font-body text-xs font-semibold tracking-[0.3em] uppercase text-[#C5A059] mb-4">
                Medical Conditions
              </h2>
              <p className="font-body text-sm text-[#A3A3A3] mb-6">
                Please check if you are affected by or have any of the following:
              </p>
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
                {conditionsList.map((condition) => (
                  <label key={condition} className="flex items-center gap-2 cursor-pointer group">
                    <input
                      type="checkbox"
                      checked={form.conditions.includes(condition)}
                      onChange={() => handleConditionToggle(condition)}
                      data-testid={`intake-condition-${condition.toLowerCase().replace(/[^a-z]/g, '_')}`}
                      className="accent-[#C5A059] w-4 h-4"
                    />
                    <span className="font-body text-sm text-[#A3A3A3] group-hover:text-white transition-colors">
                      {condition}
                    </span>
                  </label>
                ))}
              </div>
              <div className="mt-6">
                <label className="block font-body text-xs tracking-wider uppercase text-[#A3A3A3] mb-2">
                  If affected, please explain or list any other significant issues we should know about
                </label>
                <textarea name="conditions_details" value={form.conditions_details} onChange={handleChange} rows={3}
                  data-testid="intake-conditions_details" className="w-full luxury-input resize-none"
                  placeholder="Please provide details..." />
              </div>
            </div>

            {/* Consent & Signature */}
            <div className="bg-[#121212] border border-[#262626] p-6 md:p-8">
              <h2 className="font-body text-xs font-semibold tracking-[0.3em] uppercase text-[#C5A059] mb-6">
                Consent & Signature
              </h2>
              <div className="bg-[#0A0A0A] border border-[#262626] p-4 mb-6">
                <p className="font-body text-xs text-[#A3A3A3] leading-relaxed">
                  I have read the above information and have given an accurate account of the questions. If I have any concerns, I will address these with my esthetician before the service. I understand that the services offered are not a substitute for medical care and any information provided by the therapist is for educational purposes only and not diagnostically prescriptive in nature. I give permission to my esthetician to perform the facial service and will not hold the esthetician nor Luxury Beauty accountable for any liability that may result from this treatment. I understand that the information herein is to aid the therapist in giving better service and is completely confidential.
                </p>
              </div>

              <div className="grid md:grid-cols-2 gap-6 mb-8">
                <InputField label="Client Signature (Type Full Name)" name="signature" value={form.signature} onChange={handleChange} required placeholder="Type your full name as signature" />
                <InputField label="Date" name="signature_date" type="date" value={form.signature_date} onChange={handleChange} required />
              </div>

              <div className="border-t border-[#262626] pt-6">
                <label className="flex items-center gap-3 cursor-pointer mb-4">
                  <input
                    type="checkbox"
                    checked={form.minor_consent}
                    onChange={(e) => setForm((prev) => ({ ...prev, minor_consent: e.target.checked }))}
                    data-testid="intake-minor_consent"
                    className="accent-[#C5A059] w-4 h-4"
                  />
                  <span className="font-body text-sm text-[#A3A3A3]">
                    <strong className="text-white">Consent to Treatment of Minor:</strong> By signature below, I hereby authorize Luxury Beauty to administer massage, bodywork or facial to my child or dependent as they deem necessary.
                  </span>
                </label>

                {form.minor_consent && (
                  <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: "auto" }}
                    className="grid md:grid-cols-2 gap-6 mt-4">
                    <InputField label="Signature of Parent or Guardian" name="guardian_signature" value={form.guardian_signature} onChange={handleChange} placeholder="Type full name" />
                    <InputField label="Date" name="guardian_date" type="date" value={form.guardian_date} onChange={handleChange} />
                  </motion.div>
                )}
              </div>
            </div>

            {/* Submit */}
            <button
              type="submit"
              disabled={isSubmitting}
              data-testid="intake-submit-btn"
              className="w-full bg-[#C5A059] text-[#050505] font-semibold text-sm tracking-[0.15em] uppercase py-4 hover:brightness-110 transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-3"
            >
              {isSubmitting ? (
                <>
                  <Loader2 className="w-5 h-5 animate-spin" />
                  Submitting...
                </>
              ) : (
                <>
                  <Send className="w-5 h-5" />
                  Submit Intake Form
                </>
              )}
            </button>

            <p className="font-body text-xs text-[#A3A3A3] text-center">
              All information is kept strictly confidential and used only for treatment purposes.
            </p>
          </form>
        </div>
      </section>
    </div>
  );
};

export default ClientIntakeForm;
